import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CamposDeLaFila, PlantacionDeInforme } from "@/lib/informes";
import { formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { ChipDelCuadro } from "./ChipDelCuadro";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FormularioDeNuevaPlantacion } from "./FormularioDeNuevaPlantacion";
import { TablaDePlantaciones } from "./TablaDePlantaciones";
import {
  excesoSobreElPredio,
  textoDelResumen,
  vistaDelCuadro,
  type FiltroDeEstado,
  type FiltroDelCuadro,
} from "./cuadroDePlantaciones";
import {
  formularioDesdeElItem,
  type ItemDePlantacionPorEnlazar,
  type TerrenoDeLaLista,
} from "./plantacionesTerreno";
import { formatearSuperficie } from "./superficies";

interface ListaDePlantacionesProps {
  filas: PlantacionDeInforme[];
  ocupada: boolean;
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onAbrir: (fila: PlantacionDeInforme) => void;
  onValidar: (fila: PlantacionDeInforme, validada: boolean) => void;
  /** Lo que el encargado dejó en el predio; sin él la lista es la de siempre. */
  terreno?: TerrenoDeLaLista;
  /** La superficie declarada del predio, YA EN HECTÁREAS; `null` si no está. */
  superficieDelPredioHa?: number | null;
}

const FILTROS: { filtro: FiltroDeEstado; rotulo: string }[] = [
  { filtro: "sin_validar", rotulo: "Sin validar" },
  { filtro: "sin_fotos", rotulo: "Sin fotos" },
  { filtro: "incompletas", rotulo: "Incompletas" },
];

/**
 * La tarjeta 01: la lista de plantaciones del informe, AGRUPADA POR ESPECIE.
 *
 * TODO LO QUE FILTRA Y CUENTA vive en `cuadroDePlantaciones.ts`; acá solo está
 * el estado de la pantalla (qué filtro, qué búsqueda, qué grupos están
 * plegados) y su dibujo. Los grupos plegados se guardan por CLAVE de especie:
 * un grupo que cambia de posición sigue siendo el mismo plegado.
 *
 * LAS PLANTACIONES CREADAS EN TERRENO SE ADOPTAN POR EL FORMULARIO DE ALTA:
 * tocar una abre "Nueva plantación" precargada y el alta viaja con su
 * `terreno_ref`. Es el mismo POST de siempre —las plantaciones se guardan de a
 * una, no con la pestaña—, así que no hay estado de borrador que sobreviva.
 */
export function ListaDePlantaciones({
  filas,
  ocupada,
  onAgregar,
  onAbrir,
  onValidar,
  terreno,
  superficieDelPredioHa = null,
}: ListaDePlantacionesProps) {
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  /** La plantación de terreno que el formulario está completando, si la hay. */
  const [adoptando, setAdoptando] = useState<ItemDePlantacionPorEnlazar | null>(null);
  const [abiertas, setAbiertas] = useState<ReadonlySet<string>>(new Set());
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<FiltroDelCuadro>("todas");
  const [plegados, setPlegados] = useState<ReadonlySet<string>>(new Set());

  const vista = useMemo(() => vistaDelCuadro(filas, { busqueda, filtro }), [filas, busqueda, filtro]);

  const { resumen } = vista;
  const exceso = excesoSobreElPredio(resumen, superficieDelPredioHa);
  // Sin ninguna plantación —ni de terreno— el formulario va abierto: es lo único que hay.
  const porEnlazar = terreno?.porEnlazar ?? [];
  const sinNada = filas.length === 0 && porEnlazar.length === 0;
  const mostrarFormulario = formularioAbierto || sinNada;
  const todasValidadas = resumen.cantidad > 0 && resumen.validadas === resumen.cantidad;

  const clavesVisibles = vista.grupos.map((g) => g.clave);
  const todosPlegados = clavesVisibles.length > 0 && clavesVisibles.every((c) => plegados.has(c));

  function alternarGrupo(clave: string) {
    setPlegados((actual) => {
      const siguiente = new Set(actual);

      if (!siguiente.delete(clave)) siguiente.add(clave);

      return siguiente;
    });
  }

  function alternarRegistros(clave: string) {
    setAbiertas((actuales) => {
      const siguientes = new Set(actuales);

      if (!siguientes.delete(clave)) siguientes.add(clave);

      return siguientes;
    });
  }

  function cerrarElFormulario() {
    setFormularioAbierto(false);
    setAdoptando(null);
  }

  function plegarODesplegarTodo() {
    setPlegados(todosPlegados ? new Set() : new Set(clavesVisibles));
  }

  return (
    <section
      id="plantaciones-lista"
      aria-labelledby="plantaciones-lista-titulo"
      className="bg-card scroll-mt-5 overflow-hidden rounded-xl border-[1.5px]"
    >
      <div className="px-5 pt-4 pb-3">
        <EncabezadoNumerado
          numero="01"
          idDelTitulo="plantaciones-lista-titulo"
          titulo={
            resumen.cantidad > 0
              ? `${resumen.cantidad} ${resumen.cantidad === 1 ? "plantación" : "plantaciones"}`
              : "Plantaciones"
          }
          bajada={textoDelResumen(resumen)}
          acciones={
            <>
              {resumen.cantidad > 0 ? (
                <span
                  className={cn(
                    "rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold",
                    todasValidadas
                      ? "bg-success-soft text-success-soft-foreground"
                      : "bg-muted text-ink-body",
                  )}
                >
                  {resumen.validadas} de {resumen.cantidad} validadas
                </span>
              ) : null}
              {resumen.conSuperficie ? (
                <span className="bg-muted text-ink rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold">
                  {formatearSuperficie(resumen.superficieHa)} ha
                </span>
              ) : null}
              {resumen.plantas === null ? null : (
                <span className="bg-muted text-ink rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold">
                  {formatearMonto(resumen.plantas, null)} plantas
                </span>
              )}
              {mostrarFormulario ? null : (
                <Button
                  type="button"
                  size="sm"
                  // El verde más fuerte del diseño (#2b8229), no el primario.
                  className="bg-primary-fuerte hover:bg-primary-fuerte hover:brightness-90"
                  onClick={() => {
                    setAdoptando(null);
                    setFormularioAbierto(true);
                  }}
                >
                  + Agregar plantación
                </Button>
              )}
            </>
          }
        />
      </div>

      {exceso ? (
        // Avisa y no bloquea: agregar y guardar siguen disponibles.
        <p
          role="status"
          className="bg-warning-soft text-warning-soft-foreground mx-5 mb-3 rounded-md px-3 py-2 text-[0.78125rem] font-semibold"
        >
          {exceso} Revisa las superficies de los cuarteles o la del predio.
        </p>
      ) : null}

      {mostrarFormulario ? (
        <FormularioDeNuevaPlantacion
          // Una plantación de terreno distinta es OTRO borrador: se remonta.
          key={adoptando?.uuid ?? "a-mano"}
          ocupada={ocupada}
          filas={filas}
          inicial={adoptando ? formularioDesdeElItem(adoptando.item) : undefined}
          onAgregar={(campos, fotos) =>
            onAgregar(adoptando ? { ...campos, terreno_ref: adoptando.uuid } : campos, fotos)
          }
          onCerrar={cerrarElFormulario}
        />
      ) : null}

      {sinNada ? null : (
        <>
          <div className="border-divisor bg-muted/30 flex flex-wrap items-center gap-2.5 border-t px-5 py-2.5">
            <div className="relative w-[14.375rem] flex-none">
              <Search
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                aria-label="Buscar variedad o cuartel"
                placeholder="Buscar variedad o cuartel"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="bg-background h-8 rounded-[0.625rem] pl-8 text-[0.78125rem]"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {FILTROS.map((f) => (
                <ChipDelCuadro
                  key={f.filtro}
                  activo={filtro === f.filtro}
                  rotulo={f.rotulo}
                  cantidad={vista.conteos[f.filtro]}
                  onClick={() => setFiltro(filtro === f.filtro ? "todas" : f.filtro)}
                />
              ))}
            </div>

            {vista.grupos.length > 0 ? (
              <button
                type="button"
                onClick={plegarODesplegarTodo}
                className="text-ink-body hover:text-ink ml-auto text-[0.71875rem] font-bold whitespace-nowrap"
              >
                {todosPlegados ? "Desplegar todo" : "Plegar todo"}
              </button>
            ) : null}
          </div>

          {vista.visibles > 0 || porEnlazar.length > 0 ? (
            <div className="border-divisor-suave overflow-x-auto border-t">
              <TablaDePlantaciones
                grupos={vista.grupos}
                plegados={plegados}
                total={vista.total}
                totalConSuperficie={resumen.conSuperficie}
                ocupada={ocupada}
                onAlternarGrupo={alternarGrupo}
                onAbrir={onAbrir}
                onValidar={onValidar}
                todas={filas}
                terreno={terreno}
                abiertas={abiertas}
                onAlternar={alternarRegistros}
                onAdoptar={(porEnlazar) => {
                  setAdoptando(porEnlazar);
                  setFormularioAbierto(true);
                }}
              />
            </div>
          ) : (
            <p className="border-divisor-suave text-ink-muted border-t px-5 py-5.5 text-center text-[0.78125rem] font-semibold">
              Ninguna plantación coincide con la búsqueda o el filtro.
            </p>
          )}

          <div className="border-divisor-suave bg-superficie-sutil text-ink-muted border-t px-5 py-2.5 text-right text-[0.6875rem] font-semibold">
            Clic en una especie para plegarla; clic en una fila para ver el detalle y editar.
          </div>
        </>
      )}
    </section>
  );
}
